import { useEffect, useMemo, useState } from 'react';
import { buildQuiz, QUESTIONS_COUNT } from '../utils/quiz.js';
import { playCorrect, playIncorrect, playOpenedCorrect } from '../utils/audio.js';
import { track } from '../utils/tracking.js';
import StateShape from './StateShape.jsx';
import OpenEdFactoid from './OpenEdFactoid.jsx';
import QuizMap from './QuizMap.jsx';
import ConfettiBurst from './ConfettiBurst.jsx';

export default function QuizScreen({ mode, scope, onFinish, onQuit }) {
  const questions = useMemo(() => buildQuiz({ mode, scope }), [mode, scope]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const q = questions[idx];

  useEffect(() => {
    track('question_shown', { mode, scope, q_index: idx, state: q?.state.abbr, isOpenEd: q?.state.isOpenEd });
  }, [idx, mode, scope, q]);

  function handleSelect(choiceId) {
    if (revealed) return;
    const choice = q.choices.find(c => c.id === choiceId);
    setSelected(choiceId);
    setRevealed(true);

    const isCorrect = choice.isCorrect;
    const isOpenEd = q.state.isOpenEd;

    if (isCorrect) {
      setScore(s => s + 1);
      if (isOpenEd) playOpenedCorrect(); else playCorrect();
    } else {
      playIncorrect();
    }

    setAnswers(prev => [...prev, {
      stateAbbr: q.state.abbr,
      stateName: q.state.name,
      isCorrect,
      isOpenEd,
      selected: choiceId,
    }]);

    track('question_answered', { mode, scope, q_index: idx, state: q.state.abbr, isCorrect, isOpenEd });
  }

  function handleNext() {
    if (idx + 1 >= questions.length) {
      onFinish({ score, total: questions.length, answers });
      return;
    }
    setIdx(i => i + 1);
    setSelected(null);
    setRevealed(false);
  }

  if (!q) return null;

  const isOpenEd = q.state.isOpenEd;
  const stageClass = [
    'stage',
    isOpenEd ? 'is-opened' : '',
    revealed && (q.choices.find(c => c.id === selected)?.isCorrect) ? 'is-correct' : '',
    revealed && !(q.choices.find(c => c.id === selected)?.isCorrect) ? 'is-incorrect' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="fade-in">
      <div className="quiz-header">
        <div className="quiz-progress">
          <span className="current">{idx + 1}</span>
          <span> / {QUESTIONS_COUNT}</span>
        </div>
        <button className="btn-quit" onClick={onQuit}>← Home</button>
        <div className="quiz-score">
          <span className="score-num">{score}</span>
          <span>correct</span>
        </div>
      </div>

      <QuizMap answers={answers} currentAbbr={q.state.abbr} compact />

      <div className="question page-slide" key={idx}>
        <div className="prompt-label">{q.promptLabel}</div>
        <div className="prompt">
          <em>{q.promptText}</em>?
        </div>
      </div>

      <div className={stageClass}>
        <div className="stage-corner corner-tl" aria-hidden="true" />
        <div className="stage-corner corner-tr" aria-hidden="true" />
        <div className="stage-corner corner-bl" aria-hidden="true" />
        <div className="stage-corner corner-br" aria-hidden="true" />

        {isOpenEd && (
          <div className="opened-ribbon" aria-hidden="true">OpenEd state</div>
        )}

        <StateShape key={q.state.abbr} state={q.state} />

        {revealed && isOpenEd && q.choices.find(c => c.id === selected)?.isCorrect && (
          <>
            <ConfettiBurst />
            <OpenEdFactoid state={q.state} />
          </>
        )}
      </div>

      <div className="choices page-slide" key={`choices-${idx}`}>
        {q.choices.map((choice, i) => {
          const letter = String.fromCharCode(65 + i);
          let cls = 'choice';
          if (revealed) {
            if (choice.id === selected) {
              cls += choice.isCorrect ? ' is-selected is-correct' : ' is-selected is-incorrect';
            } else if (choice.isCorrect) {
              cls += ' is-revealed is-correct';
            }
          }
          return (
            <button
              key={choice.id}
              className={cls}
              disabled={revealed}
              onClick={() => handleSelect(choice.id)}
            >
              <span className="choice-letter">{letter}</span>
              {choice.label}
            </button>
          );
        })}
      </div>

      {revealed && (
        <div className="next-row fade-in">
          <button className="btn-next" onClick={handleNext} autoFocus>
            {idx + 1 >= questions.length ? 'See your score' : 'Next question'} →
          </button>
        </div>
      )}
    </div>
  );
}
